// ─────────────────────────────────────────────────────────────
// Minimal JSONPath extractor
// ─────────────────────────────────────────────────────────────
//
// Supports:
//   $              → root
//   $.foo          → property access
//   $.foo.bar      → nested property access
//   $.foo[0]       → array index
//   $.foo[0].bar   → mixed
//   $[0]           → array index at root
//
// Does NOT support: wildcards, filters, recursive descent, slices,
// quoted property names with spaces. We keep it tiny on purpose —
// scenarios runner only needs to walk down a known shape.

export function extract(input: unknown, path: string): unknown {
    if (!path || typeof path !== 'string') return undefined;

    // Strip leading "$" — accept both "$" and "$.foo" and "$[0]".
    let p = path.trim();
    if (p === '$') return input;
    if (p.startsWith('$')) p = p.slice(1);

    // Tokenise: split on "." but treat "[n]" as its own step.
    // Easiest path is to parse char-by-char.
    const tokens: Array<string | number> = [];
    let i = 0;
    let buf = '';

    const flush = () => {
        if (buf.length > 0) {
            tokens.push(buf);
            buf = '';
        }
    };

    while (i < p.length) {
        const ch = p[i];
        if (ch === '.') {
            flush();
            i++;
            continue;
        }
        if (ch === '[') {
            flush();
            const close = p.indexOf(']', i);
            if (close === -1) return undefined;
            const raw = p.slice(i + 1, close).trim();
            const n = Number(raw);
            if (!Number.isFinite(n)) return undefined;
            tokens.push(n);
            i = close + 1;
            continue;
        }
        buf += ch;
        i++;
    }
    flush();

    let cur: unknown = input;
    for (const tok of tokens) {
        if (cur === null || cur === undefined) return undefined;
        if (typeof tok === 'number') {
            if (!Array.isArray(cur)) return undefined;
            cur = cur[tok];
        } else {
            if (typeof cur !== 'object') return undefined;
            cur = (cur as Record<string, unknown>)[tok];
        }
    }
    return cur;
}

// Substitute `${var}` placeholders in a string with values from a
// variable map. Missing vars are left as-is (so the user sees what
// blew up). Non-string values are coerced via String().
export function substituteVars(input: string, vars: Record<string, unknown>): string {
    return input.replace(/\$\{([^}]+)\}/g, (_match, name) => {
        const v = vars[name.trim()];
        if (v === undefined || v === null) return `\${${name}}`;
        return String(v);
    });
}

// Walk a JSON-shaped object and substitute `${var}` in any string leaf.
// Arrays + nested objects handled recursively. Non-string primitives
// pass through untouched.
export function substituteDeep(input: unknown, vars: Record<string, unknown>): unknown {
    if (typeof input === 'string') return substituteVars(input, vars);
    if (Array.isArray(input)) return input.map((v) => substituteDeep(v, vars));
    if (input && typeof input === 'object') {
        const out: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(input)) {
            out[k] = substituteDeep(v, vars);
        }
        return out;
    }
    return input;
}
